import { useEffect, useMemo, useState } from 'react';
import type { JSX, ReactNode } from 'react';
import { Button, SkeletonText, StatusBadge, cn } from '@taki/ui';
import type { Product } from '@taki/data-model';
import type { TakiSdk } from '@taki/sdk';
import { raisonDe, sac } from './morceaux.js';
import { valeurEnTexte } from './temps.js';
import {
  calculerDose,
  doseEnTexte,
  lignesDuCalcul,
  lirePoids,
  produitEnTexte,
  profilDuPatient,
  substanceEnTexte,
} from './doses.js';
import type { Etat, PosologieDuCalcul, Profil } from './doses.js';
import { nombreEnTexte } from './nombre.js';

/** Un calcul du Catalogue (porte Calculs de dose). */
interface Calcul {
  id: string;
  libelle: string;
  description: string;
  ageSenior: number | null;
}

const LIBELLES_DES_PROFILS: Readonly<Record<Profil, string>> = { Standard: 'dose standard', Senior: 'dose senior', Fragile: 'dose malade' };

function nombre(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/** Une etape du calculateur : un titre numerote, son contenu dessous. */
function Etape({ rang, titre, children }: { rang: number; titre: string; children: ReactNode }): JSX.Element {
  return (
    <div className="flex flex-col gap-2">
      <span className="flex items-center gap-2 text-xs font-semibold text-ink-500">
        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-surface-3 text-ink-700">{rang}</span>
        {titre}
      </span>
      {children}
    </div>
  );
}

/**
 * L'ONGLET « CALCULATEUR DE DOSE » du dossier (fondateur, 26/10/2026). Le
 * veterinaire CHOISIT un calcul (une anesthesie, un medicament), REMPLIT les
 * variables (poids, espece, age, etat), et lit la ou les doses. Les calculs et
 * leurs posologies viennent du Catalogue ; l'age et l'etat choisissent le profil
 * (standard, senior, malade) et le calcul dit quand il se replie faute de dose
 * propre au profil. Lecture seule : rien ne s'ecrit, la fiche ne bouge pas.
 */
export function CalculateurDeDose({
  sdk,
  espece,
  poidsDeLaFiche,
  ageDeLaFiche,
  catalogue,
}: {
  sdk: TakiSdk;
  espece: string;
  /** Le dernier poids connu (pesee, sinon fiche), en kg. */
  poidsDeLaFiche: number | null;
  /** L'age en annees au jour de la consultation. */
  ageDeLaFiche: number | null;
  catalogue: readonly Product[];
}): JSX.Element {
  const [calculs, setCalculs] = useState<readonly Calcul[] | null>(null);
  const [posologies, setPosologies] = useState<readonly PosologieDuCalcul[]>([]);
  const [erreur, setErreur] = useState<string | null>(null);
  useEffect(() => {
    let vivant = true;
    void Promise.all([sdk.policies.list({ type: 'calcul', limit: 100 }), sdk.policies.list({ type: 'posologie', limit: 500 })]).then(
      ([desCalculs, desPosologies]) => {
        if (!vivant) return;
        setCalculs(
          desCalculs
            .map((f) => ({ id: f.id, libelle: valeurEnTexte(sac(f)['libelle']) || 'Calcul', description: valeurEnTexte(sac(f)['description']), ageSenior: nombre(sac(f)['ageSenior']) }))
            .sort((a, b) => a.libelle.localeCompare(b.libelle, 'fr')),
        );
        setPosologies(
          desPosologies.map((f) => {
            const s = sac(f);
            const produit = catalogue.find((p) => p.id === s['productId']);
            const profil = s['profil'] === 'Senior' || s['profil'] === 'Fragile' ? s['profil'] : 'Standard';
            return {
              id: f.id,
              libelle: valeurEnTexte(s['libelle']),
              calculId: String(s['calculId'] ?? ''),
              profil,
              productId: String(s['productId'] ?? ''),
              produit: produit?.name ?? 'Produit introuvable',
              unite: produit?.unit ?? '',
              concentration: nombre(sac(produit)['concentration']),
              indication: valeurEnTexte(s['indication']) || 'Autre',
              especes: Array.isArray(s['species']) ? s['species'].filter((x): x is string => typeof x === 'string') : [],
              dose: nombre(s['dose']) ?? 0,
              doseMax: nombre(s['doseMax']),
              doseUnit: s['doseUnit'] === 'µg/kg' ? 'µg/kg' : 'mg/kg',
              route: valeurEnTexte(s['route']),
              note: valeurEnTexte(s['note']),
              source: valeurEnTexte(s['source']),
            };
          }),
        );
      },
      (cause: unknown) => vivant && setErreur(`Calculs illisibles : ${raisonDe(cause)}`),
    );
    return () => {
      vivant = false;
    };
  }, [sdk, catalogue]);

  /* 1. LE CALCUL */
  const [calculId, setCalculId] = useState<string | null>(null);
  const calcul = calculs?.find((c) => c.id === calculId) ?? null;

  /* 2. LES VARIABLES : reprises de l'animal, modifiables sans toucher a sa fiche. */
  const enSaisie = (n: number | null): string => (n === null ? '' : nombreEnTexte(n));
  const [poidsSaisi, setPoidsSaisi] = useState(enSaisie(poidsDeLaFiche));
  const [ageSaisi, setAgeSaisi] = useState(enSaisie(ageDeLaFiche));
  const [especeChoisie, setEspeceChoisie] = useState(espece);
  const [etat, setEtat] = useState<Etat>('Sain');
  const poids = lirePoids(poidsSaisi);
  const age = ageSaisi.trim() === '' ? null : (lirePoids(ageSaisi) ?? (ageSaisi.trim() === '0' ? 0 : null));
  const especesDuCalcul = useMemo(
    () => [...new Set(posologies.filter((p) => p.calculId === calculId).flatMap((p) => p.especes))].sort((a, b) => a.localeCompare(b, 'fr')),
    [posologies, calculId],
  );
  const especes = espece !== '' && !especesDuCalcul.includes(espece) ? [espece, ...especesDuCalcul] : especesDuCalcul;

  /* 3. LES DOSES */
  const profil = profilDuPatient(age, calcul?.ageSenior ?? null, etat);
  const lignes = calcul === null || especeChoisie === '' ? [] : lignesDuCalcul(posologies, calcul.id, especeChoisie, profil);

  const CHAMP = 'h-9 rounded-sm border border-border bg-surface px-3 text-right text-sm tabular-nums text-ink-900 focus-visible:border-accent-border focus-visible:outline-none';

  return (
    <section className="flex flex-col gap-4">
      <h3 className="border-b border-border-soft pb-2 text-base font-semibold text-ink-900">Calculateur de dose</h3>
      {erreur !== null ? <p className="text-sm text-danger">{erreur}</p> : null}

      <div className="flex flex-col gap-4 rounded-md border border-border bg-surface-1 px-4 py-3">
        <Etape rang={1} titre="Calcul">
          {calculs === null && erreur === null ? <SkeletonText lines={2} /> : null}
          {calculs !== null && calculs.length === 0 ? (
            <p className="text-sm text-ink-500">Aucun calcul : ils se posent dans le Catalogue, porte Calculs de dose.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {(calculs ?? []).map((c) => (
                <Button key={c.id} size="sm" variant={c.id === calculId ? 'dark' : 'secondary'} onClick={() => setCalculId(c.id)}>
                  {c.libelle}
                </Button>
              ))}
            </div>
          )}
          {calcul === null || calcul.description === '' ? null : <p className="text-xs text-ink-500">{calcul.description}</p>}
        </Etape>

        {calcul === null ? null : (
          <Etape rang={2} titre="Patient">
            <div className="flex flex-wrap items-end gap-6">
              <label className="flex flex-col gap-1">
                <span className="text-xs text-ink-500">Poids (kg)</span>
                <input className={cn(CHAMP, 'w-24')} value={poidsSaisi} inputMode="decimal" aria-label="Poids en kilos" onChange={(e) => setPoidsSaisi(e.target.value)} />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-ink-500">Âge (ans)</span>
                <input className={cn(CHAMP, 'w-20')} value={ageSaisi} inputMode="decimal" aria-label="Âge en années" onChange={(e) => setAgeSaisi(e.target.value)} />
              </label>
              <div className="flex flex-col gap-1">
                <span className="text-xs text-ink-500">Espèce</span>
                <div className="flex flex-wrap gap-2">
                  {especes.map((e) => (
                    <Button key={e} size="sm" variant={e === especeChoisie ? 'dark' : 'secondary'} onClick={() => setEspeceChoisie(e)}>
                      {e}
                    </Button>
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-xs text-ink-500">État</span>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant={etat === 'Sain' ? 'dark' : 'secondary'} onClick={() => setEtat('Sain')}>
                    Sain (ASA I et II)
                  </Button>
                  <Button size="sm" variant={etat === 'Malade' ? 'dark' : 'secondary'} onClick={() => setEtat('Malade')}>
                    Malade (ASA III et plus)
                  </Button>
                </div>
              </div>
            </div>
            {(poidsDeLaFiche !== null && poids !== poidsDeLaFiche) || (ageDeLaFiche !== null && age !== ageDeLaFiche) || especeChoisie !== espece ? (
              <div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setPoidsSaisi(enSaisie(poidsDeLaFiche));
                    setAgeSaisi(enSaisie(ageDeLaFiche));
                    setEspeceChoisie(espece);
                  }}
                >
                  Reprendre les valeurs de l'animal
                </Button>
              </div>
            ) : null}
          </Etape>
        )}
      </div>

      {calcul === null ? null : (
        <div className="flex flex-col gap-3">
          <span className="flex items-center gap-2 text-xs font-semibold text-ink-500">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-surface-3 text-ink-700">3</span>
            Doses
            <StatusBadge
              label={profil === 'Standard' ? 'Profil standard' : profil === 'Senior' ? `Profil senior (${nombreEnTexte(calcul.ageSenior ?? 0)} ans et plus)` : 'Profil malade'}
              tone={profil === 'Standard' ? 'neutral' : 'accent'}
            />
          </span>
          {poids === null ? <p className="text-sm text-ink-500">Indique le poids pour calculer les doses.</p> : null}
          {lignes.length === 0 ? (
            <p className="text-sm text-ink-500">Aucune posologie de ce calcul pour l'espèce « {especeChoisie || 'non renseignée'} ».</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border-soft text-left text-xs text-ink-500">
                  <th className="w-36 py-2 font-semibold">Étape</th>
                  <th className="py-2 font-semibold">Médicament</th>
                  <th className="w-36 py-2 font-semibold">Dose</th>
                  <th className="w-20 py-2 font-semibold">Voie</th>
                  <th className="w-28 py-2 text-right font-semibold">Substance</th>
                  <th className="w-36 py-2 text-right font-semibold">À administrer</th>
                </tr>
              </thead>
              <tbody>
                {lignes.map((l) => {
                  const resultat = poids === null ? null : calculerDose(poids, l);
                  const quantite = resultat === null ? '' : produitEnTexte(resultat, l.unite);
                  return (
                    <tr key={l.id} className="border-b border-border-soft align-top">
                      <td className="py-2 pr-3 text-ink-700">{l.indication}</td>
                      <td className="py-2 pr-3">
                        <span className="flex flex-wrap items-center gap-2 text-ink-900">
                          {l.produit}
                          {l.repli ? <StatusBadge label={`Pas de ${LIBELLES_DES_PROFILS[profil]} : ${LIBELLES_DES_PROFILS[l.profil]}`} tone="danger" /> : null}
                        </span>
                        {l.note === '' ? null : <span className="block text-xs text-ink-700">{l.note}</span>}
                        {l.source === '' ? null : <span className="block text-xs text-ink-400">{l.source}</span>}
                      </td>
                      <td className="py-2 pr-3 tabular-nums text-ink-700">{doseEnTexte(l)}</td>
                      <td className="py-2 pr-3 text-ink-700">{l.route}</td>
                      <td className="py-2 text-right tabular-nums text-ink-700">{resultat === null ? '' : substanceEnTexte(resultat, l.doseUnit)}</td>
                      <td className={cn('py-2 text-right tabular-nums', quantite === '' ? 'text-ink-400' : 'font-semibold text-ink-900')}>
                        {resultat === null ? '' : quantite === '' ? 'Concentration manquante' : quantite}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </section>
  );
}
